import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const DEFAULT_LIMITS = {
  maxUsers: 0,
  maxQuotes: 0,
  maxDesignAttachmentBytes: 20 * 1024 * 1024,
  maxDesignAttachments: 5,
  maxAIConversations: 0,
  maxAITurnCount: 0,
  maxStorageBytes: 0,
  hasApprovalWorkflows: false,
};

const toFiniteNumber = (value, fallback = 0) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
};

export const formatBytes = (bytes) => {
  const normalized = toFiniteNumber(bytes, 0);
  if (normalized <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unitIndex = 0;
  let value = normalized;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = value >= 10 || unitIndex === 0 ? Math.round(value) : value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
};

const buildCountLimitMessage = (label, used, max) => `${label} limit reached (${used} of ${max} used)`;

const hasValidTenantId = (tenantId) => {
  const value = String(tenantId ?? '').trim();
  return value !== '' && value.toLowerCase() !== 'null' && value.toLowerCase() !== 'undefined';
};

const getStoredTenantId = () => {
  if (typeof window === 'undefined') return '';
  return String(window.localStorage.getItem('tenantID') || '').trim();
};

const useTenantPlanUsage = ({ includeUsers = false, includeQuotes = false, tenantId = '' } = {}) => {
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [usage, setUsage] = useState({
    usedUsers: 0,
    usedQuotes: 0,
    usedStorageBytes: 0,
    effectiveStorageBytes: 0,
    aiConversationsThisMonth: 0,
    storageBreakdown: null,
  });
  const [loading, setLoading] = useState(true);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [billingCurrency, setBillingCurrency] = useState('');

  const [refreshCounter, setRefreshCounter] = useState(0);
  const refresh = useCallback(() => setRefreshCounter((c) => c + 1), []);
  const effectiveTenantId = useMemo(() => {
    const providedTenantId = String(tenantId || '').trim();
    if (hasValidTenantId(providedTenantId)) {
      return providedTenantId;
    }
    const storedTenantId = getStoredTenantId();
    return hasValidTenantId(storedTenantId) ? storedTenantId : '';
  }, [tenantId]);

  useEffect(() => {
    let isMounted = true;

    const fetchUsage = async () => {
      if (!hasValidTenantId(effectiveTenantId)) {
        if (isMounted) {
          setLimits(DEFAULT_LIMITS);
          setUsage({
            usedUsers: 0,
            usedQuotes: 0,
            usedStorageBytes: 0,
            effectiveStorageBytes: 0,
            aiConversationsThisMonth: 0,
            storageBreakdown: null,
          });
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);

        const requests = [
          axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/billing/subscriptions/current`),
        ];

        if (includeUsers) {
          requests.push(
            axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/users`, {
              params: { page: 1, limit: 1 },
            })
          );
        }

        if (includeQuotes) {
          requests.push(axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/quote/usage`));
        }

        const responses = await Promise.all(requests);
        const subscriptionResponse = responses[0];
        const nextLimits = subscriptionResponse.data?.subscription?.planSnapshot?.limits
          || subscriptionResponse.data?.subscription?.limits
          || DEFAULT_LIMITS;
        const tenantUsage = subscriptionResponse.data?.usage || {};
        const subscriptionData = subscriptionResponse.data?.subscription || {};
        if (isMounted) {
          setTrialEndsAt(subscriptionData.trialEndsAt || null);
          setBillingCurrency(String(subscriptionResponse.data?.billingCurrency || '').trim().toUpperCase());
        }

        let responseIndex = 1;
        const usersResponse = includeUsers ? responses[responseIndex++] : null;
        const quotesResponse = includeQuotes ? responses[responseIndex] : null;

        if (!isMounted) {
          return;
        }

        setLimits({
          maxUsers: toFiniteNumber(nextLimits?.maxUsers, DEFAULT_LIMITS.maxUsers),
          maxQuotes: toFiniteNumber(nextLimits?.maxQuotes, DEFAULT_LIMITS.maxQuotes),
          maxDesignAttachmentBytes: toFiniteNumber(
            nextLimits?.maxDesignAttachmentBytes,
            DEFAULT_LIMITS.maxDesignAttachmentBytes
          ),
          maxDesignAttachments: toFiniteNumber(
            nextLimits?.maxDesignAttachments,
            DEFAULT_LIMITS.maxDesignAttachments
          ),
          maxAIConversations: toFiniteNumber(nextLimits?.maxAIConversations, DEFAULT_LIMITS.maxAIConversations),
          maxAITurnCount: toFiniteNumber(nextLimits?.maxAITurnCount, DEFAULT_LIMITS.maxAITurnCount),
          maxStorageBytes: toFiniteNumber(nextLimits?.maxStorageBytes, DEFAULT_LIMITS.maxStorageBytes),
          hasApprovalWorkflows: nextLimits?.hasApprovalWorkflows === true,
        });

        setUsage({
          usedUsers: includeUsers ? toFiniteNumber(usersResponse?.data?.totalResults, 0) : 0,
          usedQuotes: includeQuotes ? toFiniteNumber(quotesResponse?.data?.usedQuotes, 0) : 0,
          usedStorageBytes: toFiniteNumber(tenantUsage.usedStorageBytes, 0),
          effectiveStorageBytes: toFiniteNumber(tenantUsage.effectiveStorageBytes, 0),
          aiConversationsThisMonth: toFiniteNumber(tenantUsage.aiConversationsThisMonth, 0),
          storageBreakdown: tenantUsage.storageBreakdown || null,
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setLimits(DEFAULT_LIMITS);
        setUsage({
          usedUsers: 0,
          usedQuotes: 0,
          usedStorageBytes: 0,
          effectiveStorageBytes: 0,
          aiConversationsThisMonth: 0,
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUsage();

    return () => {
      isMounted = false;
    };
  }, [effectiveTenantId, includeQuotes, includeUsers, refreshCounter]);

  const trialDaysLeft = useMemo(() => {
    if (!trialEndsAt) return null;
    const diff = new Date(trialEndsAt) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days >= 0 ? days : null;
  }, [trialEndsAt]);

  return useMemo(() => {
    const maxUsers = toFiniteNumber(limits.maxUsers, DEFAULT_LIMITS.maxUsers);
    const maxQuotes = toFiniteNumber(limits.maxQuotes, DEFAULT_LIMITS.maxQuotes);
    const maxDesignAttachmentBytes = toFiniteNumber(
      limits.maxDesignAttachmentBytes,
      DEFAULT_LIMITS.maxDesignAttachmentBytes
    );
    const maxDesignAttachments = toFiniteNumber(
      limits.maxDesignAttachments,
      DEFAULT_LIMITS.maxDesignAttachments
    );
    const maxAIConversations = toFiniteNumber(limits.maxAIConversations, DEFAULT_LIMITS.maxAIConversations);
    const maxAITurnCount = toFiniteNumber(limits.maxAITurnCount, DEFAULT_LIMITS.maxAITurnCount);
    const maxStorageBytes = toFiniteNumber(limits.maxStorageBytes, DEFAULT_LIMITS.maxStorageBytes);
    const hasApprovalWorkflows = limits.hasApprovalWorkflows === true;

    const usedUsers = toFiniteNumber(usage.usedUsers, 0);
    const usedQuotes = toFiniteNumber(usage.usedQuotes, 0);
    const usedStorageBytes = toFiniteNumber(usage.usedStorageBytes, 0);
    const effectiveStorageBytes = toFiniteNumber(usage.effectiveStorageBytes, 0) || maxStorageBytes;
    const aiConversationsThisMonth = toFiniteNumber(usage.aiConversationsThisMonth, 0);
    const storageBreakdown = usage.storageBreakdown || null;

    const canCreateUser = maxUsers <= 0 || usedUsers < maxUsers;
    const canCreateQuote = maxQuotes <= 0 || usedQuotes < maxQuotes;
    const canUpload = effectiveStorageBytes <= 0 || usedStorageBytes < effectiveStorageBytes;
    const canStartAIConversation = maxAIConversations <= 0 || aiConversationsThisMonth < maxAIConversations;

    return {
      loading,
      refresh,
      maxUsers,
      usedUsers,
      canCreateUser,
      userLimitMessage: canCreateUser ? '' : buildCountLimitMessage('User', usedUsers, maxUsers),
      maxQuotes,
      usedQuotes,
      canCreateQuote,
      quoteLimitMessage: canCreateQuote ? '' : buildCountLimitMessage('Quote', usedQuotes, maxQuotes),
      maxDesignAttachmentBytes,
      maxDesignAttachments,
      designAttachmentLimitLabel: formatBytes(maxDesignAttachmentBytes),
      maxAIConversations,
      maxAITurnCount,
      aiConversationsThisMonth,
      canStartAIConversation,
      aiConversationLimitMessage: canStartAIConversation
        ? ''
        : buildCountLimitMessage('AI conversation', aiConversationsThisMonth, maxAIConversations),
      maxStorageBytes,
      effectiveStorageBytes,
      usedStorageBytes,
      canUpload,
      storageLimitMessage: canUpload ? '' : 'Storage quota exceeded. Delete files or buy more storage.',
      usedStorageLabel: formatBytes(usedStorageBytes),
      effectiveStorageLabel: formatBytes(effectiveStorageBytes),
      storageBreakdown,
      hasApprovalWorkflows,
      trialEndsAt,
      trialDaysLeft,
      billingCurrency,
    };
  }, [limits, loading, refresh, usage, trialEndsAt, trialDaysLeft, billingCurrency]);
};

export default useTenantPlanUsage;
